import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthHeader } from '@/hooks/useAuth';
import { getWarehouseTree } from '@/lib/api/warehouse';
import {
  getLatestDailyStoreSkuDefaults,
  startDailyStoreSkuExtraction,
} from '@/lib/api/analysis-data-extraction';
import type { WarehouseTreeNode } from '@/lib/types';
import type {
  DailyStoreSkuDefaults,
  StoreAggregationMode,
} from '@/lib/types/feature/analysis-data-extraction';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WarehouseQuickSelect } from './WarehouseQuickSelect';
import { WarehouseMultiSelect } from './WarehouseMultiSelect';
import { expandGroupsToWarehouseIds, topLevelGroupIds } from '@/lib/utils/warehouse-tree';
import { StoreGranularitySelect } from './StoreGranularitySelect';
import { StoreGroupExclusiveSelect } from './StoreGroupExclusiveSelect';

/** 予測期間（日数）の範囲。goya_prediction のAthenaパーティション保持が既定92日のため */
const PREDICTION_PERIOD_DAYS_MIN = 1;
const PREDICTION_PERIOD_DAYS_MAX = 92;
/** 実績・在庫の各期間の上限日数。バックエンドと同じ値（横持ち出力の列数に直結するため厳しめ） */
const PERIOD_MAX_DAYS = 366;
const DEFAULT_PREDICTION_PERIOD_DAYS = 30;

interface DailyStoreSkuFormProps {
  account: { id: string; code: string; name: string };
  onSubmitted?: () => void;
}

/** 開始日・終了日の日数（両端含む）を計算する。不正な日付なら null */
function periodDays(start: string, end: string): number | null {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  if (Number.isNaN(startTime) || Number.isNaN(endTime)) return null;
  return Math.round((endTime - startTime) / (24 * 60 * 60 * 1000)) + 1;
}

/** 昨年実績期間のオフセット日数（52週。曜日を揃えるため暦日ではなく週数で決める） */
const PRIOR_YEAR_OFFSET_DAYS = 364;
/** 初期表示の今年実績期間・在庫期間の長さ（日数） */
const DEFAULT_ACTUAL_PERIOD_DAYS = 28;

/**
 * 実行時点の「今日」を `YYYY-MM-DD` で返す。
 *
 * ここだけはローカル時刻のフィールドを読む（利用者が今日と思っている暦日を起点にしたいため。
 * UTCで読むとJSTの午前0〜9時に前日が起点になってしまう）。
 * これ以降の加減算は `shiftDate` に任せ、UTCで閉じる。
 */
function todayInLocalCalendar(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * `YYYY-MM-DD` を日数ずらす。空文字や不正な日付なら空文字。
 *
 * 解釈も整形もUTCで閉じる。`YYYY-MM-DD` は UTC 0時として解釈されるため、整形側で
 * ローカル時刻のフィールドを読むとUTCより西のタイムゾーンで1日ずれる
 * （WeeklyStoreProductGroupForm の期間上限計算と同じ方針）。
 */
function shiftDate(value: string, days: number): string {
  if (!value) return '';
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return '';
  return new Date(time + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * 前回実行がないときの初期値。すべて空だと6つの日付を本日から手で送る必要があるため、
 * 「昨日を終端にした直近4週間」を起点に置き、昨年実績・在庫期間はそこから導出する。
 * 予測断面を昨日にするのは goya_prediction の断面が日次生成で、当日分は未生成のことがあるため。
 */
function buildInitialDates(now: Date) {
  const yesterday = shiftDate(todayInLocalCalendar(now), -1);
  const actualStart = shiftDate(yesterday, -(DEFAULT_ACTUAL_PERIOD_DAYS - 1));
  return {
    predictionBaseDate: yesterday,
    actualStartDate: actualStart,
    actualEndDate: yesterday,
    stockStartDate: actualStart,
    stockEndDate: yesterday,
    priorYearActualStartDate: shiftDate(actualStart, -PRIOR_YEAR_OFFSET_DAYS),
    priorYearActualEndDate: shiftDate(yesterday, -PRIOR_YEAR_OFFSET_DAYS),
  };
}

/**
 * [日別] 店舗×SKU 予測/実績/在庫抽出の実行フォーム（FKV3-12588）
 * 対象倉庫・予測断面/期間・今年/昨年実績期間・在庫期間を入力し、6ファイルのZIPを非同期生成する。
 */
export function DailyStoreSkuForm({ account, onSubmitted }: DailyStoreSkuFormProps) {
  const { getAuthHeader } = useAuthHeader();

  const [targetWarehouseIds, setTargetWarehouseIds] = useState<Set<string>>(new Set());
  const [targetInitialized, setTargetInitialized] = useState(false);
  const [warehouseNodes, setWarehouseNodes] = useState<WarehouseTreeNode[]>([]);
  const warehouseIds = useMemo(
    () => warehouseNodes.filter((n) => n.node_type === 'warehouse').map((n) => n.id),
    [warehouseNodes],
  );
  // 初期値は「昨日を終端にした直近4週間」。空だと6つの日付を本日から手で送ることになる
  const initialDates = useMemo(() => buildInitialDates(new Date()), []);
  const [predictionBaseDate, setPredictionBaseDate] = useState(initialDates.predictionBaseDate);
  const [predictionPeriodDays, setPredictionPeriodDays] = useState(DEFAULT_PREDICTION_PERIOD_DAYS);
  const [actualStartDate, setActualStartDate] = useState(initialDates.actualStartDate);
  const [actualEndDate, setActualEndDate] = useState(initialDates.actualEndDate);
  const [priorYearActualStartDate, setPriorYearActualStartDate] = useState(
    initialDates.priorYearActualStartDate,
  );
  const [priorYearActualEndDate, setPriorYearActualEndDate] = useState(
    initialDates.priorYearActualEndDate,
  );
  const [stockStartDate, setStockStartDate] = useState(initialDates.stockStartDate);
  const [stockEndDate, setStockEndDate] = useState(initialDates.stockEndDate);

  // 店舗軸の出力粒度。店舗グループ別を選ぶと店舗側4ファイルが店舗グループ単位に丸まる（FKV3-12857）
  const [storeAggregationMode, setStoreAggregationMode] =
    useState<StoreAggregationMode>('by_seller');
  // 集計する店舗グループ（選んだグループがそのまま出力の1行になる。週次メニューと同じ規則。FKV3-12858）
  const [storeGroupNodeIds, setStoreGroupNodeIds] = useState<Set<string>>(new Set());
  const [groupSelectionInitialized, setGroupSelectionInitialized] = useState(false);
  const isByGroup = storeAggregationMode === 'by_group';

  // 実際に送信する対象倉庫。前回実行条件から復元した倉庫が削除されている場合があるため、
  // 現存する倉庫だけに絞る。検証も送信もこの値だけを見る
  // （生の選択集合を見ると、復元した倉庫が全て削除済みのときに「選択あり」と誤判定し、
  //  絞ったつもりが空配列＝全倉庫で送信されてしまう）
  // 店舗グループ別では選んだグループの配下が出力範囲になる（範囲と集計単位を1つのツリーで選ぶ）
  const effectiveTargetWarehouseIds = useMemo(() => {
    if (isByGroup) {
      const expanded = expandGroupsToWarehouseIds(warehouseNodes, storeGroupNodeIds);
      return warehouseIds.filter((id) => expanded.has(id));
    }
    return warehouseIds.filter((id) => targetWarehouseIds.has(id));
  }, [isByGroup, warehouseNodes, warehouseIds, storeGroupNodeIds, targetWarehouseIds]);

  // 昨年実績期間・在庫期間を今年実績期間から自動で決めるか（既定ON）。
  // ONの間は今年実績期間の変更に追従し、入力欄は読み取り専用にする（追従で上書きされる値を
  // 手で編集できると、どちらが効くのか分からなくなるため）。
  const [priorYearAuto, setPriorYearAuto] = useState(true);
  const [stockAuto, setStockAuto] = useState(true);

  const [pendingDefaults, setPendingDefaults] = useState<DailyStoreSkuDefaults | null | undefined>(
    undefined,
  );
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 初回マウント時点で account.code が既に分かっていれば直後の取得effectが走るため true から始める
  // （アカウント切替時は下のレンダー中の調整で true に戻す）
  const [mastersLoading, setMastersLoading] = useState(() => Boolean(account.code));

  // アカウント切替時に前アカウントの入力・選択を持ち越さないようリセットする
  // （レンダー中の調整パターン。理由は WeeklyStoreProductGroupForm と同じ）
  const prevAccountCodeRef = useRef(account.code);
  if (account.code !== prevAccountCodeRef.current) {
    prevAccountCodeRef.current = account.code;
    setTargetWarehouseIds(new Set());
    setTargetInitialized(false);
    setWarehouseNodes([]);
    setPredictionBaseDate(initialDates.predictionBaseDate);
    setPredictionPeriodDays(DEFAULT_PREDICTION_PERIOD_DAYS);
    setActualStartDate(initialDates.actualStartDate);
    setActualEndDate(initialDates.actualEndDate);
    setPriorYearActualStartDate(initialDates.priorYearActualStartDate);
    setPriorYearActualEndDate(initialDates.priorYearActualEndDate);
    setStockStartDate(initialDates.stockStartDate);
    setStockEndDate(initialDates.stockEndDate);
    setPriorYearAuto(true);
    setStockAuto(true);
    setStoreAggregationMode('by_seller');
    setStoreGroupNodeIds(new Set());
    setGroupSelectionInitialized(false);
    setPendingDefaults(undefined);
    setDefaultsApplied(false);
    setError(null);
    setMastersLoading(true);
  }

  // 倉庫マスタ・前回実行条件の取得（リセットは上のレンダー中の調整で完了済み）
  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();
    const treePromise = getWarehouseTree(account.code, authHeaders)
      .then((nodes) => {
        if (!cancelled) setWarehouseNodes(nodes);
      })
      .catch(() => {
        if (!cancelled) setError('倉庫一覧の取得に失敗しました');
      });
    const defaultsPromise = getLatestDailyStoreSkuDefaults(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setPendingDefaults(res.has_previous_execution ? res.parameters : null);
      })
      .catch(() => {
        if (!cancelled) setPendingDefaults(null);
      });
    Promise.allSettled([treePromise, defaultsPromise]).then(() => {
      if (!cancelled) setMastersLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 倉庫取得後、対象倉庫を初回のみ全チェックにする（前回実行条件が「全倉庫」＝空配列の場合も同様）
  useEffect(() => {
    if (!targetInitialized && warehouseIds.length > 0) {
      setTargetWarehouseIds(new Set(warehouseIds));
      setTargetInitialized(true);
    }
  }, [warehouseIds, targetInitialized]);

  // 店舗グループ別を初めて選んだときは最上位グループを既定にする（全社1行から始める）。
  // 一度初期化したら、利用者が全解除した状態を勝手に埋め直さない
  useEffect(() => {
    if (!isByGroup || groupSelectionInitialized || warehouseNodes.length === 0) return;
    setStoreGroupNodeIds(topLevelGroupIds(warehouseNodes));
    setGroupSelectionInitialized(true);
  }, [isByGroup, groupSelectionInitialized, warehouseNodes]);

  // 前回実行条件をフォームへ反映（アカウント切替ごとに1回のみ）
  useEffect(() => {
    if (defaultsApplied || pendingDefaults === undefined) return;
    if (pendingDefaults) {
      setPredictionBaseDate(pendingDefaults.prediction_base_date);
      setPredictionPeriodDays(pendingDefaults.prediction_period_days);
      setActualStartDate(pendingDefaults.actual_start_date);
      setActualEndDate(pendingDefaults.actual_end_date);
      setPriorYearActualStartDate(pendingDefaults.prior_year_actual_start_date);
      setPriorYearActualEndDate(pendingDefaults.prior_year_actual_end_date);
      setStockStartDate(pendingDefaults.stock_start_date);
      setStockEndDate(pendingDefaults.stock_end_date);
      // FKV3-12857 より前のジョブには店舗軸の集計単位が無いため、店舗別（現行動作）に落とす
      setStoreAggregationMode(pendingDefaults.store_aggregation_mode ?? 'by_seller');
      // FKV3-12858 より前のジョブは選択を持たないため、既定（最上位グループ）に委ねる
      if (pendingDefaults.store_group_node_ids?.length) {
        setStoreGroupNodeIds(new Set(pendingDefaults.store_group_node_ids));
        setGroupSelectionInitialized(true);
      }
      // 前回値が自動導出と一致していれば追従を維持し、手で変えていたならOFFにして値を守る
      setPriorYearAuto(
        pendingDefaults.prior_year_actual_start_date ===
          shiftDate(pendingDefaults.actual_start_date, -PRIOR_YEAR_OFFSET_DAYS) &&
          pendingDefaults.prior_year_actual_end_date ===
            shiftDate(pendingDefaults.actual_end_date, -PRIOR_YEAR_OFFSET_DAYS),
      );
      setStockAuto(
        pendingDefaults.stock_start_date === pendingDefaults.actual_start_date &&
          pendingDefaults.stock_end_date === pendingDefaults.actual_end_date,
      );
      if (pendingDefaults.target_warehouse_ids.length > 0) {
        setTargetWarehouseIds(new Set(pendingDefaults.target_warehouse_ids));
        setTargetInitialized(true);
      }
    }
    setDefaultsApplied(true);
  }, [pendingDefaults, defaultsApplied]);

  // 今年実績期間の変更を、追従がONの昨年実績期間・在庫期間へ反映する。
  // effect ではなくイベントハンドラで同期する（追従OFFへ切り替えた直後に effect が
  // 走って手入力を上書きする、という順序依存を作らないため）。
  const changeActualStart = (value: string) => {
    setActualStartDate(value);
    if (priorYearAuto) setPriorYearActualStartDate(shiftDate(value, -PRIOR_YEAR_OFFSET_DAYS));
    if (stockAuto) setStockStartDate(value);
  };
  const changeActualEnd = (value: string) => {
    setActualEndDate(value);
    if (priorYearAuto) setPriorYearActualEndDate(shiftDate(value, -PRIOR_YEAR_OFFSET_DAYS));
    if (stockAuto) setStockEndDate(value);
  };
  // 追従をONに戻した時点で今年実績期間から作り直す（ONなのに古い値が残るのを防ぐ）
  const changePriorYearAuto = (checked: boolean) => {
    setPriorYearAuto(checked);
    if (checked) {
      setPriorYearActualStartDate(shiftDate(actualStartDate, -PRIOR_YEAR_OFFSET_DAYS));
      setPriorYearActualEndDate(shiftDate(actualEndDate, -PRIOR_YEAR_OFFSET_DAYS));
    }
  };
  const changeStockAuto = (checked: boolean) => {
    setStockAuto(checked);
    if (checked) {
      setStockStartDate(actualStartDate);
      setStockEndDate(actualEndDate);
    }
  };

  const validatePeriod = (label: string, start: string, end: string): string | null => {
    if (!start || !end) return `${label}を指定してください`;
    if (start > end) return `${label}は開始日が終了日以前になるよう指定してください`;
    const days = periodDays(start, end);
    if (days !== null && days > PERIOD_MAX_DAYS) {
      return `${label}は最大${PERIOD_MAX_DAYS}日です`;
    }
    return null;
  };

  const validate = (): string | null => {
    if (!predictionBaseDate) return '予測断面を指定してください';
    if (
      predictionPeriodDays < PREDICTION_PERIOD_DAYS_MIN ||
      predictionPeriodDays > PREDICTION_PERIOD_DAYS_MAX
    ) {
      return `予測期間は${PREDICTION_PERIOD_DAYS_MIN}〜${PREDICTION_PERIOD_DAYS_MAX}日の範囲で指定してください`;
    }
    const actualError = validatePeriod('今年実績期間', actualStartDate, actualEndDate);
    if (actualError) return actualError;
    const priorYearError = validatePeriod(
      '昨年実績期間',
      priorYearActualStartDate,
      priorYearActualEndDate,
    );
    if (priorYearError) return priorYearError;
    const stockError = validatePeriod('在庫期間', stockStartDate, stockEndDate);
    if (stockError) return stockError;
    if (isByGroup && storeGroupNodeIds.size === 0) {
      return '集計する店舗グループを1つ以上選択してください';
    }
    if (warehouseIds.length > 0 && effectiveTargetWarehouseIds.length === 0) {
      return isByGroup
        ? '選択した店舗グループに倉庫が紐づいていません'
        : '対象倉庫を1つ以上選択してください';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    // 対象倉庫が全選択なら空配列で送る（バックエンドで全倉庫扱い。IN句の肥大化を避ける）。
    // 全選択の判定に件数比較を使うため、倉庫の削除と追加が同数でも誤判定しないよう
    // 現存分に絞った effectiveTargetWarehouseIds で比較する
    const allTargetSelected =
      warehouseIds.length > 0 && effectiveTargetWarehouseIds.length === warehouseIds.length;
    const warehouseIdsToSend = allTargetSelected ? [] : effectiveTargetWarehouseIds;

    try {
      setSubmitting(true);
      setError(null);
      const authHeaders = getAuthHeader();
      await startDailyStoreSkuExtraction(
        account.code,
        {
          target_warehouse_ids: warehouseIdsToSend,
          prediction_base_date: predictionBaseDate,
          prediction_period_days: predictionPeriodDays,
          actual_start_date: actualStartDate,
          actual_end_date: actualEndDate,
          prior_year_actual_start_date: priorYearActualStartDate,
          prior_year_actual_end_date: priorYearActualEndDate,
          stock_start_date: stockStartDate,
          stock_end_date: stockEndDate,
          store_aggregation_mode: storeAggregationMode,
          // 店舗別のときは選択を送らない（バックエンドが「切り替えたつもりが効いていない」を弾く）
          store_group_node_ids: isByGroup ? Array.from(storeGroupNodeIds) : [],
        },
        authHeaders,
      );
      toast.success('抽出ジョブを受け付けました。実行ログで進捗を確認できます');
      onSubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : '抽出ジョブの実行に失敗しました';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>抽出条件</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* 店舗軸の出力粒度（FKV3-12857）。直下のツリーの意味が変わるため、その直前に置く（FKV3-13085） */}
        <StoreGranularitySelect
          value={storeAggregationMode}
          onValueChange={setStoreAggregationMode}
          idPrefix="daily-store-granularity"
          disabled={mastersLoading}
        />

        {/* 対象倉庫 */}
        <div className="space-y-2">
          <Label>
            {isByGroup
              ? '集計する店舗グループ（選んだグループごとに1行を出力）'
              : '対象倉庫（既定は全倉庫。検索・一括選択が可能）'}
          </Label>
          <p className="text-sm text-muted-foreground">
            {isByGroup ? (
              <>
                選んだグループが<strong>そのまま出力の1行</strong>
                になり、その配下が今年/昨年販売数・今年予測数・今年/昨年在庫の4ファイルの対象範囲になります。親と子は同時に選べません。
              </>
            ) : (
              <>
                今年/昨年販売数・今年予測数・今年/昨年在庫の4ファイルの<strong>対象範囲</strong>
                を、選んだ範囲に紐づく店舗に絞り込みます。
              </>
            )}
            倉庫在庫ファイルはこの選択に関わらず常に全量（物流倉庫＋発注残）を出力します。
          </p>
          {isByGroup ? (
            <StoreGroupExclusiveSelect
              nodes={warehouseNodes}
              selectedIds={storeGroupNodeIds}
              onChange={setStoreGroupNodeIds}
              idPrefix="daily-target-sg"
              loading={mastersLoading}
            />
          ) : (
            <div className="space-y-2">
              <WarehouseQuickSelect
                accountCode={account.code}
                selectedIds={targetWarehouseIds}
                onChange={setTargetWarehouseIds}
                idPrefix="daily-target-wh"
                disabled={mastersLoading}
              />
              <WarehouseMultiSelect
                nodes={warehouseNodes}
                selectedIds={targetWarehouseIds}
                onChange={setTargetWarehouseIds}
                idPrefix="daily-target-wh"
                loading={mastersLoading}
              />
            </div>
          )}
        </div>

        {/* 予測断面・予測期間 */}
        <div className="space-y-2">
          <Label htmlFor="prediction-base-date">予測断面</Label>
          <div className="flex items-center gap-2">
            <Input
              id="prediction-base-date"
              type="date"
              value={predictionBaseDate}
              onChange={(e) => setPredictionBaseDate(e.target.value)}
              className="w-auto"
              disabled={mastersLoading}
            />
            <Label htmlFor="prediction-period-days" className="ml-4">
              予測期間（日数）
            </Label>
            <Input
              id="prediction-period-days"
              type="number"
              min={PREDICTION_PERIOD_DAYS_MIN}
              max={PREDICTION_PERIOD_DAYS_MAX}
              value={predictionPeriodDays}
              onChange={(e) => setPredictionPeriodDays(Number(e.target.value))}
              className="w-24"
              disabled={mastersLoading}
            />
          </div>
          <p className="text-sm text-gray-500">
            予測期間は最大{PREDICTION_PERIOD_DAYS_MAX}
            日です（Athenaのパーティション保持期間による制約）
          </p>
        </div>

        {/* 今年実績期間。昨年実績期間・在庫期間の基準になる */}
        <div className="space-y-2">
          <Label>今年実績期間（未来日を含めて指定できます）</Label>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="今年実績期間の開始日"
              value={actualStartDate}
              onChange={(e) => changeActualStart(e.target.value)}
              className="w-auto"
              disabled={mastersLoading}
            />
            <span>〜</span>
            <Input
              type="date"
              aria-label="今年実績期間の終了日"
              value={actualEndDate}
              onChange={(e) => changeActualEnd(e.target.value)}
              className="w-auto"
              disabled={mastersLoading}
            />
          </div>
          <p className="text-sm text-gray-500">
            下の昨年実績期間・在庫期間はこの期間から自動で決まります（個別に指定したい場合は
            チェックを外してください）
          </p>
        </div>

        {/* 昨年実績期間。既定は今年実績期間の364日前に追従 */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>昨年実績期間</Label>
            <label className="flex items-center gap-1 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={priorYearAuto}
                onChange={(e) => changePriorYearAuto(e.target.checked)}
                disabled={mastersLoading}
              />
              今年実績期間の364日前を使う
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="昨年実績期間の開始日"
              value={priorYearActualStartDate}
              onChange={(e) => setPriorYearActualStartDate(e.target.value)}
              className="w-auto"
              disabled={mastersLoading || priorYearAuto}
            />
            <span>〜</span>
            <Input
              type="date"
              aria-label="昨年実績期間の終了日"
              value={priorYearActualEndDate}
              onChange={(e) => setPriorYearActualEndDate(e.target.value)}
              className="w-auto"
              disabled={mastersLoading || priorYearAuto}
            />
          </div>
        </div>

        {/* 在庫期間。既定は今年実績期間と同じ */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>在庫期間</Label>
            <label className="flex items-center gap-1 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={stockAuto}
                onChange={(e) => changeStockAuto(e.target.checked)}
                disabled={mastersLoading}
              />
              今年実績期間と同じにする
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              aria-label="在庫期間の開始日"
              value={stockStartDate}
              onChange={(e) => setStockStartDate(e.target.value)}
              className="w-auto"
              disabled={mastersLoading || stockAuto}
            />
            <span>〜</span>
            <Input
              type="date"
              aria-label="在庫期間の終了日"
              value={stockEndDate}
              onChange={(e) => setStockEndDate(e.target.value)}
              className="w-auto"
              disabled={mastersLoading || stockAuto}
            />
          </div>
          <p className="text-sm text-gray-500">
            昨年在庫はこの期間を364日ずらして自動的に出力します（別途入力は不要です）
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            抽出を実行
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
